import * as React from "react";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";

type ImageUploaderProps = {
  id?: string;
  label?: string;
  hint?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  // optional constraints
  maxSizeMB?: number;
  accept?: string[]; // e.g. ["image/png","image/jpeg","image/webp"]
  disabled?: boolean;
};

export function ImageUploader({
  id = "image-uploader",
  label = "Profilbild (optional)",
  hint = "PNG, JPG oder WebP • max. 5 MB",
  value,
  onChange,
  maxSizeMB = 5,
  accept = ["image/*"],
  disabled,
}: ImageUploaderProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // build accept attribute string
  const acceptAttr = React.useMemo(() => accept.join(","), [accept]);

  // keep preview in sync (createObjectURL for File)
  React.useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  function validate(file: File) {
    setError(null);
    if (
      !accept.some((a) => {
        if (a.endsWith("/*")) return file.type.startsWith(a.replace("/*", "/"));
        return file.type === a || a === "image/*";
      })
    ) {
      setError("Dateityp wird nicht unterstützt.");
      return false;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      setError(`Datei ist zu groß (${sizeMB.toFixed(1)} MB). Maximal ${maxSizeMB} MB.`);
      return false;
    }
    return true;
  }

  function pickFile(file?: File) {
    if (!file) return;
    if (!validate(file)) return;
    setBusy(true);
    // simulate tiny processing delay for UX (e.g. EXIF read, could be removed)
    setTimeout(() => {
      onChange(file);
      setBusy(false);
    }, 50);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    pickFile(file);
    // reset input so selecting same file again re-triggers change
    if (inputRef.current) inputRef.current.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    pickFile(file);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function openFileDialog() {
    inputRef.current?.click();
  }

  function clear() {
    onChange(null);
    setError(null);
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4" /> {label}
      </Label>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={acceptAttr}
        onChange={onInputChange}
        className="hidden"
        disabled={disabled}
      />

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFileDialog();
          }
        }}
        onClick={openFileDialog}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        aria-disabled={disabled}
        className={[
          "flex flex-col items-center justify-center rounded-2xl border border-dashed p-6 text-center transition",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          isDragging ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-muted-foreground/30",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40",
        ].join(" ")}>
        {previewUrl ? (
          <div className="flex w-full flex-col items-center gap-4">
            {/* Preview */}
            <img src={previewUrl} alt="Bildvorschau" className="h-40 w-40 rounded-2xl object-cover" />
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={openFileDialog} disabled={disabled || busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Bild ändern
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={disabled || busy}>
                <X className="mr-2 h-4 w-4" />
                Entfernen
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="h-8 w-8" />
            <div className="text-sm">
              <span className="font-medium underline underline-offset-4">Datei wählen</span> oder hierher ziehen
            </div>
            <div className="text-xs text-muted-foreground">{hint}</div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
